import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminHttpClient } from '../../api/client.js';
import { TabStrip } from '../../components/TabStrip.js';
import {
  SPEECH_PRESETS,
  SPEECH_PROTOCOLS,
  SPEECH_STT_MAX_INPUT_DURATION_MS,
  SPEECH_TTS_MAX_INPUT_CODE_POINTS,
  VOICE_SESSION_HARD_CAP_MS,
  type RealtimeVoiceAdminWrite,
  type RealtimeVoicePolicy,
  type SpeechPresetId,
  type SpeechToTextPolicy,
  type TenantVoiceAdminView,
  type TextToSpeechPolicy,
  type VoiceAdminModel,
  type VoiceModelAdminWrite,
} from '../../../../shared-types/src/realtime.js';
import { describeError } from '../../errors.js';
import { VoiceRateEditor } from './VoiceRateEditor.js';

type Kind = 'stt' | 'tts' | 'realtime';
type Policies = { stt: SpeechToTextPolicy; tts: TextToSpeechPolicy; realtime: RealtimeVoicePolicy };
/** A draft remembers the server value it started from, so a newer server value is a conflict, not an overwrite. */
type Drafts = { [K in Kind]?: { base: Policies[K] | undefined; value: Policies[K] } };

// Dictation and read-aloud first; realtime stays one tab away.
const KINDS: readonly Kind[] = ['stt', 'tts', 'realtime'];
const MODEL_KIND: Record<Kind, VoiceAdminModel['kind']> = { stt: 'speech_to_text', tts: 'text_to_speech', realtime: 'realtime_voice' };
const READINESS = ['configured', 'adapterReady', 'rateReady', 'dataReady', 'allowed'] as const;
// No list means every assigned model, as for STT/TTS.
const initialRealtime: RealtimeVoicePolicy = {
  enabled: false,
  maxSessionDurationMs: 600000,
  maxConcurrentSessions: 1,
  requirePreTransmissionInspection: false,
  requireDurableSharedBudget: false,
};
const LIMITS = { stt: SPEECH_STT_MAX_INPUT_DURATION_MS, tts: SPEECH_TTS_MAX_INPUT_CODE_POINTS, realtime: VOICE_SESSION_HARD_CAP_MS };
const limitOf = (kind: Kind, policy: Policies[Kind]) => kind === 'stt' ? (policy as SpeechToTextPolicy).maxInputDurationMs
  : kind === 'tts' ? (policy as TextToSpeechPolicy).maxInputCodePoints : (policy as RealtimeVoicePolicy).maxSessionDurationMs;
const validLimit = (kind: Kind, policy: Policies[Kind]) => {
  const value = limitOf(kind, policy);
  return Number.isSafeInteger(value) && value > 0 && value <= LIMITS[kind] && (kind === 'tts' || value % 1000 === 0);
};
const presetsOf = (kind: Kind) => (Object.keys(SPEECH_PRESETS) as SpeechPresetId[])
  .filter(id => SPEECH_PROTOCOLS[SPEECH_PRESETS[id].protocol].kind === MODEL_KIND[kind]);

/**
 * The selected tenant's administrator voice controls. Each kind saves only its own
 * child against `policyVersion`; drafts of the other kinds survive saves, model
 * actions and tab switches. Mounted per tenant, so another tenant never sees them.
 */
export function TenantVoicePanel({ http, tenantId }: { http: AdminHttpClient; tenantId: string }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<Kind>('stt');
  const [view, setView] = useState<TenantVoiceAdminView>();
  const [drafts, setDrafts] = useState<Drafts>({});
  const [pricingId, setPricingId] = useState<string>();
  const [busy, setBusy] = useState(true);
  const [loadError, setLoadError] = useState<string>();
  const [actionError, setActionError] = useState<{ message: string; modelId?: string }>();
  const [savedKind, setSavedKind] = useState<Kind>();
  const [reload, setReload] = useState(0);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  useEffect(() => {
    let active = true;
    pending.current = true;
    setBusy(true);
    setLoadError(undefined);
    void http.getTenantVoice(tenantId).then(next => {
      if (active) setView(next);
    }).catch(cause => {
      if (active) setLoadError(describeError(cause, t, 'voiceAdmin.loadFailed'));
    }).finally(() => {
      if (active) { pending.current = false; setBusy(false); }
    });
    return () => { active = false; };
  }, [http, tenantId, reload, t]);

  async function run(task: () => Promise<TenantVoiceAdminView>, modelId?: string, saved?: Kind) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setActionError(undefined);
    setSavedKind(undefined);
    try {
      const next = await task();
      if (!mounted.current) return;
      setView(next);
      if (saved) {
        setDrafts(current => { const rest = { ...current }; delete rest[saved]; return rest; });
        setSavedKind(saved);
      }
    } catch (cause) {
      if (mounted.current) setActionError({ message: describeError(cause, t, 'voiceAdmin.failed'), ...(modelId ? { modelId } : {}) });
    } finally {
      if (mounted.current) { pending.current = false; setBusy(false); }
    }
  }

  // Live models keep their legacy writer; speech models use the voice model endpoint.
  const modelAction = (model: VoiceAdminModel, action: 'activate' | 'disable') => run(model.kind === 'realtime_voice'
    ? async () => { await http.putRealtimeVoice(tenantId, { action, modelId: model.id } satisfies RealtimeVoiceAdminWrite); return http.getTenantVoice(tenantId); }
    : () => http.postTenantVoiceModel(tenantId, { action, modelId: model.id } satisfies VoiceModelAdminWrite), model.id);

  function reloadServer() {
    if (pending.current) return;
    setDrafts({});
    setActionError(undefined);
    setSavedKind(undefined);
    setReload(value => value + 1);
  }

  const policies: Policies | undefined = view && {
    stt: drafts.stt?.value ?? view.stt,
    tts: drafts.tts?.value ?? view.tts,
    realtime: drafts.realtime?.value ?? view.realtime ?? initialRealtime,
  };
  const edit = <K extends Kind>(target: K, patch: Partial<Policies[K]>) => {
    if (!policies || !view) return;
    setSavedKind(undefined);
    setDrafts(current => ({ ...current, [target]: { base: current[target] ? current[target]!.base : view[target],
      value: { ...policies[target], ...patch } } }));
  };
  function save(target: Kind) {
    if (!view || !policies) return;
    const draft = drafts[target];
    // A model action or another kind's save re-reads the view; a newer value of this kind must not be overwritten silently.
    if (draft && JSON.stringify(draft.base) !== JSON.stringify(view[target])) {
      setSavedKind(undefined);
      setActionError({ message: t('errors.codes.voice_policy_conflict') });
      return;
    }
    void run(() => http.putTenantVoice(tenantId, { expectedPolicyVersion: view.policyVersion, [target]: policies[target] }), undefined, target);
  }
  const models = view?.models.filter(model => model.kind === MODEL_KIND[kind]) ?? [];
  const policy = policies?.[kind];
  // An absent list means every assigned model; unassigned models can never be allowed.
  const allowedIds = policy?.allowedModelIds ?? models.filter(model => model.readiness.allowed).map(model => model.id);

  return <section className="panel realtime-voice-admin" aria-label={t('voiceAdmin.title')} aria-busy={busy}>
    <h3>{t('voiceAdmin.title')}</h3>
    <p className="platform-scope-badge">{t('voiceAdmin.scope')}</p>
    <p className="screen-help">{t('voiceAdmin.visibility')}</p>
    <p className="screen-help">{t('voiceAdmin.accessScope')}</p>
    <p className="screen-help">{t('voiceAdmin.notice')}</p>
    {loadError ? <div role="alert"><p>{loadError}</p>
      <button type="button" className="secondary" disabled={busy} onClick={reloadServer}>{t('voiceAdmin.reload')}</button>
    </div> : null}
    <TabStrip tabs={KINDS.map(id => ({ id, label: t(`voiceAdmin.kinds.${id}`) }))} value={kind}
      onChange={next => { setKind(next); setPricingId(undefined); setSavedKind(undefined); }}
      ariaLabel={t('voiceAdmin.kindNavigation')} level="nested" />
    {view && policies && policy ? <div role="tabpanel" aria-label={t(`voiceAdmin.kinds.${kind}`)}>
      {actionError && !actionError.modelId ? <div role="alert"><p>{actionError.message}</p>
        <button type="button" className="secondary" disabled={busy} onClick={reloadServer}>{t('voiceAdmin.reload')}</button>
      </div> : null}
      <fieldset className="form-grid" disabled={busy}>
        <legend>{t('voiceAdmin.models')}</legend>
        <div className="realtime-voice-actions">
          {kind === 'realtime'
            ? (['openai', 'gemini'] as const).map(provider => <button type="button" className="secondary" key={provider}
              disabled={view.models.some(model => model.id === `${view.tenantId}-voice-${provider}`)}
              onClick={() => void run(async () => { await http.putRealtimeVoice(tenantId, { action: 'register', provider }); return http.getTenantVoice(tenantId); })}>
              {t('voiceAdmin.registerRealtime', { provider })}
            </button>)
            : presetsOf(kind).map(preset => <button type="button" className="secondary" key={preset}
              disabled={view.models.some(model => model.id === `${view.tenantId}-speech-${preset}`)}
              onClick={() => void run(() => http.postTenantVoiceModel(tenantId, { action: 'register', preset }))}>
              {t('voiceAdmin.registerPreset', { name: SPEECH_PRESETS[preset].displayName })}
            </button>)}
        </div>
        {models.length === 0 ? <p className="muted">{t('voiceAdmin.noModels')}</p> : null}
        <div className="realtime-voice-models">
          {models.map(model => <article className="realtime-voice-model" key={model.id}>
            <div className="realtime-voice-model-header">
              <label className="check-row">
                <input type="checkbox" checked={allowedIds.includes(model.id)} disabled={!model.readiness.allowed && !allowedIds.includes(model.id)}
                  onChange={event => edit(kind, { allowedModelIds: event.target.checked
                    ? [...new Set([...allowedIds, model.id])] : allowedIds.filter(id => id !== model.id) })} />
                <span><strong>{model.displayName}</strong><small>{model.id}</small></span>
              </label>
              <span className={`badge status-${model.status}`}>{t(`voiceAdmin.status.${model.status}`)}</span>
            </div>
            <ul className="voice-readiness" aria-label={t('voiceAdmin.readiness')}>
              {READINESS.map(key => <li key={key} className={model.readiness[key] ? 'ready' : 'missing'}>
                {t(`voiceAdmin.ready.${key}`)}: {t(model.readiness[key] ? 'voiceAdmin.yes' : 'voiceAdmin.no')}
              </li>)}
            </ul>
            {!model.readiness.adapterReady ? <p className="muted">{t('voiceAdmin.adapterPending')}</p> : null}
            <div className="realtime-voice-actions">
              <button type="button" className="secondary" aria-expanded={pricingId === model.id}
                onClick={() => setPricingId(pricingId === model.id ? undefined : model.id)}>{t('voiceRates.title')}</button>
              <button type="button" disabled={model.status === 'active'} onClick={() => void modelAction(model, 'activate')}>{t('voiceAdmin.activate')}</button>
              <button type="button" className="secondary danger" disabled={model.status === 'disabled'}
                onClick={() => void modelAction(model, 'disable')}>{t('voiceAdmin.disable')}</button>
            </div>
            {actionError?.modelId === model.id ? <p role="alert">{actionError.message}</p> : null}
            {pricingId === model.id ? <VoiceRateEditor key={model.id} http={http} modelId={model.id} protocol={model.protocol} /> : null}
          </article>)}
        </div>
        <label className="check-row">
          <input type="checkbox" checked={policy.enabled} onChange={event => edit(kind, { enabled: event.target.checked })} />
          <span>{t(`voiceAdmin.allow.${kind}`)}</span>
        </label>
        {kind === 'stt' ? <label>{t('voiceAdmin.sttSeconds')}
          <input type="number" min={1} max={SPEECH_STT_MAX_INPUT_DURATION_MS / 1000} step={1} value={policies.stt.maxInputDurationMs / 1000}
            onChange={event => edit('stt', { maxInputDurationMs: Number(event.target.value) * 1000 })} />
        </label> : null}
        {kind === 'tts' ? <label>{t('voiceAdmin.ttsCharacters')}
          <input type="number" min={1} max={SPEECH_TTS_MAX_INPUT_CODE_POINTS} step={1} value={policies.tts.maxInputCodePoints}
            onChange={event => edit('tts', { maxInputCodePoints: Number(event.target.value) })} />
        </label> : null}
        {kind === 'realtime' ? <>
          <label>{t('voiceAdmin.realtimeSeconds')}
            <input type="number" min={1} max={VOICE_SESSION_HARD_CAP_MS / 1000} step={1} value={policies.realtime.maxSessionDurationMs / 1000}
              onChange={event => edit('realtime', { maxSessionDurationMs: Number(event.target.value) * 1000 })} />
          </label>
          <label className="check-row">
            <input type="checkbox" checked={policies.realtime.requirePreTransmissionInspection}
              onChange={event => edit('realtime', { requirePreTransmissionInspection: event.target.checked })} />
            <span>{t('voiceAdmin.inspection')}</span>
          </label>
          <label className="check-row">
            <input type="checkbox" checked={policies.realtime.requireDurableSharedBudget}
              onChange={event => edit('realtime', { requireDurableSharedBudget: event.target.checked })} />
            <span>{t('voiceAdmin.sharedBudget')}</span>
          </label>
        </> : null}
        <div className="form-actions">
          <button type="button" disabled={!validLimit(kind, policy)} onClick={() => save(kind)}>{t('voiceAdmin.save')}</button>
        </div>
      </fieldset>
      {savedKind === kind ? <p role="status">{t('voiceAdmin.saved')}</p> : null}
    </div> : null}
  </section>;
}
